import { useQuery } from "@tanstack/react-query";
import { apiDownload, apiFetchText } from "./client";

/**
 * The in-app user manual (ADR 0008): Markdown and screenshots embedded in the API
 * binary and served behind the same token check as `/api/v1/me` — the SPA's
 * own static files are public, so a manual bundled there would be readable
 * without signing in.
 *
 * The API only ever serves `.md`, `.png`, `.webp` and `.jpg`/`.jpeg`, and only
 * inside `de/` or `en/`; anything else, a directory, or a path with `..` is a
 * 404. `Cache-Control: private, max-age=300` — `staleTime` below matches it, so
 * a query does not refetch a chapter more often than the response it would get
 * back permits caching for anyway.
 */
const MANUAL_STALE_TIME_MS = 5 * 60 * 1000;

/** Builds the path for one file inside a language's manual. */
export function manualPath(language: string, file: string): string {
  return `/api/v1/manual/${language}/${file}`;
}

export const manualQueryKey = (language: string, file: string) =>
  ["manual", language, file] as const;

/** Fetches one manual file (a chapter, or `toc.md`) as Markdown text. */
export function useManualMarkdown(language: string, file: string) {
  return useQuery({
    queryKey: manualQueryKey(language, file),
    queryFn: ({ signal }) => apiFetchText(manualPath(language, file), { signal }),
    staleTime: MANUAL_STALE_TIME_MS,
    retry: false,
  });
}

/** The table of contents: a Markdown list of links to the chapters. */
export function useManualToc(language: string) {
  return useManualMarkdown(language, "toc.md");
}

/** The Markdown for one chapter (`index`, `dashboard`, `budgets`, …). */
export function useManualChapter(language: string, slug: string) {
  return useManualMarkdown(language, `${slug}.md`);
}

/**
 * Downloads a screenshot the same way `apiDownload` downloads an export: with
 * the bearer token attached, handed back as a `Blob` for the caller to turn
 * into an object URL. A plain `<img src>` cannot do this — the token is a
 * bearer header, not a cookie, so the image would arrive unauthenticated and
 * the browser would render the API's 401 page instead of a screenshot.
 */
export async function fetchManualImage(language: string, path: string): Promise<Blob> {
  const { blob } = await apiDownload(manualPath(language, path), path);
  return blob;
}
